import React, { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle, Typography, Box, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useDispatch } from "react-redux";
import ScanDNI from "../../../../../../../components/common/PL/ScanDNI/ScanDNI";
import { useTranslation } from "../../../../../../../hooks/useTranslation";
import { useStore } from "../../../../../../../hooks/useStore";
import UserSDK from "../../../../../../../passapp-sdk/user";
import DialogConfirmation from "../../../../../../../components/common/PL/Dialog/DialogConfirmation";
import Select from "../../../../../../../components/common/PL/Select/Select";
import TextInput from "../../../../../../../components/common/PL/TextInput/TextInput";
import Button from "../../../../../../../components/common/PL/Button/Button";
import { countryListData } from "../../../../../../../utils/countries";
import { selectCamera } from "../../../../../../../redux/actions/types/cameraTypes";
import { ISearchUser } from "../../../../../../../store/types/userTypes";
import usePLScannerListener from "../../../../../hooks/usePLScannerListener";

interface CompanionScanModalProps {
  open: boolean;
  onClose: () => void;
  onFound: (userData: any, dni: string, country: string) => void;
  onNotFound: (dni: string, country: string) => void;
  onSkip: () => void;
}

const CompanionScanModal: React.FC<CompanionScanModalProps> = ({
  open,
  onClose,
  onFound,
  onNotFound,
  onSkip,
}) => {
  const dispatch = useDispatch();
  const { t, BundleKey } = useTranslation();
  const { authStore, cameraStore } = useStore();
  const authToken = authStore.token;
  const authBuildings = authStore?.buildings || [];
  const [country, setCountry] = useState("ARG");
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [buildingDialogOpen, setBuildingDialogOpen] = useState(false);
  const [pendingPassScanData, setPendingPassScanData] = useState<{
    pass: string;
    passData: any;
    guest: any;
  } | null>(null);
  const [scanKey, setScanKey] = useState(0);
  const [isQrOrPass, setIsQrOrPass] = useState(false);
  const [dni, setDni] = useState("");
  const [scannedDni, setScannedDni] = useState("");
  const [scannedCountry, setScannedCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const dialogContentRef = useRef<HTMLDivElement>(null);

  // Stop camera tracks scoped to this dialog when it closes
  useEffect(() => {
    if (!open && dialogContentRef.current) {
      dialogContentRef.current.querySelectorAll("video").forEach((video) => {
        const stream = video.srcObject as MediaStream;
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
          video.srcObject = null;
        }
      });
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setDni("");
      setCountry("ARG");
      setLoading(false);
    }
  }, [open]);

  const trimmedDni = dni.trim();
  const canSubmit = Boolean(country && trimmedDni) && !loading;

  const handleManualSearch = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      const userSdk = new UserSDK();
      const response = await userSdk.findUserByDNI(authToken, trimmedDni, country);
      onClose();
      if (response && response.data) {
        onFound(response.data, trimmedDni, country);
      } else {
        onNotFound(trimmedDni, country);
      }
    } catch (error) {
      console.error("Error searching companion:", error);
      onClose();
      onNotFound(trimmedDni, country);
    } finally {
      setLoading(false);
    }
  };

  const handleScanResult = useCallback(
    async (results: ISearchUser) => {
      // Limpiar cualquier char que haya filtrado al input manual antes de que
      // el burst del scanner fuera detectado (el primer char siempre se filtra).
      setDni("");
      const documentNumber = results.dni?.trim();
      const documentCountry = results.countryCode;
      setScannedDni(documentNumber || "");
      setScannedCountry(documentCountry || "");
      const isMrzResult = Boolean(results.firstname && results.lastname);
      const isQrResult = Boolean(results.rawQr);
      const isPassResult = Boolean(results.rawPass);
      const isQrOrPassScan = isQrResult || isPassResult;
      setIsQrOrPass(isQrOrPassScan);

      if (isQrResult || isPassResult) {
        console.log("Scanned QR result for companion:", results);
      }

      if (!isQrOrPassScan && (!documentNumber || !documentCountry)) return;

      setLoading(true);
      try {
        const userSdk = new UserSDK();
        let response;

        if (isMrzResult) {
          response = await userSdk.findUserByMRZ(authToken, {
            dni: documentNumber!,
            country: documentCountry!,
            firstname: results.firstname!,
            lastname: results.lastname!,
          });
        } else if (isQrResult) {
          response = { data: results.userData };
          if (!response.data) throw new Error("Usuario no encontrado");
        } else if (isPassResult) {
          const passData = results.userData;
          const guest = passData?.guest || passData?.guests?.[0] || passData?.guests || passData;
          if (!guest) throw new Error("Pase no encontrado");

          const officeBuildings = authBuildings;
          const passBuildingId = passData?.building;
          const matchesBuilding = officeBuildings.some(
            (b: any) => String(b) === String(passBuildingId)
          );

          if (!matchesBuilding && passBuildingId) {
            setPendingPassScanData({
              pass: results.rawPass!,
              passData: passData,
              guest,
            });
            setBuildingDialogOpen(true);
            return;
          }

          const inviteId = passData.inviteId || passData.id || guest?.inviteId || guest?.id;
          const enrichedGuest = {
            ...guest,
            passType: passData.type,
            passId: inviteId,
            passGuestsType: passData.guestsType,
            isBuildingMismatch: !matchesBuilding,
          };
          response = { data: enrichedGuest };
        } else {
          response = await userSdk.findUserByDNI(authToken, documentNumber!, documentCountry!);
        }

        onClose();
        if (response && response.data) {
          const mergedData = {
            ...response.data,
            sex: results.sex || response.data.sex,
            birthdate: results.birthdate || response.data.birthdate,
          };
          onFound(
            mergedData,
            documentNumber || response.data.dni || "",
            documentCountry || response.data.countryCode || ""
          );
        } else {
          onNotFound(documentNumber || "", documentCountry || "");
        }
      } catch (error: any) {
        console.error("Error searching companion after scan:", error);
        setErrorMessage(error || "Error al buscar el acompañante");
        setErrorDialogOpen(true);
      } finally {
        setLoading(false);
      }
    },
    [authBuildings, authToken, onClose, onFound, onNotFound]
  );

  usePLScannerListener({ enabled: open, onScan: handleScanResult });

  const handleConfirmBuildingMismatch = () => {
    setBuildingDialogOpen(false);
    if (pendingPassScanData) {
      const { guest, passData } = pendingPassScanData;
      onClose();
      const inviteId = passData?.inviteId || passData?.id || guest?.inviteId || guest?.id;
      const enrichedGuest = {
        ...guest,
        passType: passData?.type,
        passId: inviteId,
        passGuestsType: passData?.guestsType,
        isBuildingMismatch: true,
      };
      onFound(enrichedGuest, guest.dni || "", guest.countryCode || "");
      setPendingPassScanData(null);
    }
  };

  const handleCancelBuildingMismatch = () => {
    setBuildingDialogOpen(false);
    setPendingPassScanData(null);
    setScanKey((prev) => prev + 1);
    setLoading(false);
  };

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
    setScanKey((prev) => prev + 1);
    setLoading(false);
    if (!isQrOrPass) {
      onClose();
      onNotFound(scannedDni, scannedCountry);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: { borderRadius: "16px", padding: "8px" },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" style={{ fontWeight: 700 }}>
            {t(BundleKey.DETAIL_COMPATION_TITLE)}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent ref={dialogContentRef} style={{ padding: "0 24px 24px 24px" }}>
        <div className="companionScanModal-container">
          {cameraStore.cameras.length > 1 && (
            <div className="scanDni-camera-selector">
              <label className="scanDni-camera-label">Cámara</label>
              <select
                className="scanDni-camera-select"
                value={cameraStore.deviceId}
                onChange={(e) => {
                  const device = cameraStore.cameras.find(
                    (camera: any) => camera.deviceId === e.target.value
                  );
                  if (device) dispatch(selectCamera(device));
                }}
              >
                {cameraStore.cameras.map((camera: any, index: number) => (
                  <option key={camera.deviceId} value={camera.deviceId}>
                    {camera.label || `Cámara ${index + 1}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="companionScanModal-viewport">
            {open && (
              <ScanDNI
                key={scanKey}
                onScan={handleScanResult}
                onBack={onClose}
                showCameraSelector={false}
              />
            )}
          </div>

          <div className="companionScanModal-manualSection">
            <Box display="flex" alignItems="center" my={3}>
              <Box flex={1} height="1px" bgcolor="#e2e8f0" />
              <Box mx={2}>
                <Typography variant="body2" style={{ color: "#64748b", fontWeight: 500 }}>
                  O INGRESAR MANUALMENTE
                </Typography>
              </Box>
              <Box flex={1} height="1px" bgcolor="#e2e8f0" />
            </Box>

            <div className="companionScanModal-manualRow">
              <Select
                value={country}
                onChange={(val) => setCountry(val as string)}
                options={countryListData.map((c) => ({ value: c.value, label: c.name }))}
                placeholder={t(BundleKey.MANUALINPUT_COUNTRY)}
                className="companionScanModal-countrySelect"
                isSearchable
              />
              <TextInput
                className="companionScanModal-dniInput"
                value={dni}
                onChange={(value) => setDni(value)}
                placeholder={t(BundleKey.MANUALINPUT_DNI)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleManualSearch();
                }}
              />
              <Button
                className="companionScanModal-submitButton"
                disabled={!canSubmit}
                onClick={handleManualSearch}
                loading={loading}
              >
                {t(BundleKey.MANUALINPUT_SUBMIT)}
              </Button>
            </div>
            {/* 
            <Box mt={3} display="flex" justifyContent="center">
              <Typography
                onClick={() => {
                  onClose();
                  onSkip();
                }}
                style={{
                  cursor: "pointer",
                  color: "#2B7FFF",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  textDecoration: "underline",
                }}
              >
                Omitir y completar todo manualmente
              </Typography>
            </Box> */}
          </div>
        </div>
      </DialogContent>
      <DialogConfirmation
        open={errorDialogOpen}
        onClose={handleErrorDialogClose}
        onConfirm={handleErrorDialogClose}
        title="Error"
        message={errorMessage}
        confirmLabel="Aceptar"
        cancelLabel="Cancelar"
      />
      <DialogConfirmation
        open={buildingDialogOpen}
        onClose={handleCancelBuildingMismatch}
        onConfirm={handleConfirmBuildingMismatch}
        title="Advertencia"
        message="Esta autorización es para otro establecimiento"
        confirmLabel="Continuar"
        cancelLabel="Cancelar"
      />
    </Dialog>
  );
};

export default CompanionScanModal;
